<template>
  <div class="cdr-overview-tab">
    <!-- KPI Cards -->
    <div class="kpi-grid">
      <div class="kpi-card kpi-card--primary" @click="$emit('go-to-tab', 'pending')">
        <div class="kpi-header">
          <div class="kpi-icon kpi-icon--primary">
            <v-icon>mdi-file-clock-outline</v-icon>
          </div>
          <span class="kpi-badge kpi-badge--danger" v-if="backlog.count > 0">{{ $t('cdr-dashboard.overview.immediate-action') }}</span>
        </div>
        <div class="kpi-content">
          <p class="kpi-label">{{ $t('cdr-dashboard.overview.pending-cdrs') }}</p>
          <h4 class="kpi-value">{{ backlog.count || 0 }}</h4>
          <span class="kpi-subtitle">{{ $t('cdr-dashboard.overview.current-state') }}</span>
        </div>
      </div>

      <div class="kpi-card kpi-card--danger">
        <div class="kpi-header">
          <div class="kpi-icon kpi-icon--danger">
            <v-icon>mdi-history</v-icon>
          </div>
          <span class="kpi-badge kpi-badge--danger-outline" v-if="oldestDays >= 3">{{ $t('cdr-dashboard.overview.critical-threshold') }}</span>
        </div>
        <div class="kpi-content">
          <p class="kpi-label">{{ $t('cdr-dashboard.overview.oldest-cdr') }}</p>
          <h4 class="kpi-value kpi-value--danger">{{ oldestDays }} {{ $t('cdr-dashboard.overview.days') }}</h4>
          <span class="kpi-subtitle">{{ $t('cdr-dashboard.overview.based-on-pending') }}</span>
        </div>
      </div>
    </div>

    <!-- Statistiques Historiques Section -->
    <div class="section-header">
      <h3 class="section-title">{{ $t('cdr-dashboard.overview.historical-stats') }}</h3>
      <div class="section-line"></div>
    </div>

    <div class="stats-grid">
      <!-- Left Column: Small Stats + Donut -->
      <div class="stats-column">
        <div class="small-stats-grid">
          <div class="small-stat-card">
            <div class="small-stat-header">
              <v-icon small class="mr-2">mdi-block-helper</v-icon>
              <div>
                <span class="small-stat-label">{{ $t('cdr-dashboard.overview.blocked-cdrs-hist') }}</span>
                <span class="small-stat-period">{{ $t('cdr-dashboard.overview.last-7-days') }}</span>
              </div>
            </div>
            <h4 class="small-stat-value">{{ stats.blockedCount || 0 }}</h4>
            <div class="small-stat-trend trend--success" v-if="stats.blockedTrend">
              <v-icon x-small>mdi-trending-down</v-icon>
              {{ stats.blockedTrend }}% {{ $t('cdr-dashboard.overview.vs-last-month') }}
            </div>
          </div>

          <div class="small-stat-card">
            <div class="small-stat-header">
              <v-icon small class="mr-2">mdi-auto-fix</v-icon>
              <div>
                <span class="small-stat-label">{{ $t('cdr-dashboard.overview.auto-fixed') }}</span>
                <span class="small-stat-period">{{ $t('cdr-dashboard.overview.last-7-days') }}</span>
              </div>
            </div>
            <h4 class="small-stat-value">{{ stats.autoFixedCount || 0 }}</h4>
            <div class="small-stat-trend trend--primary" v-if="stats.autoFixTrend">
              <v-icon x-small>mdi-trending-up</v-icon>
              +{{ stats.autoFixTrend }}% {{ $t('cdr-dashboard.overview.vs-last-month') }}
            </div>
          </div>

          <div class="small-stat-card">
            <div class="small-stat-header">
              <v-icon small class="mr-2">mdi-account-wrench</v-icon>
              <div>
                <span class="small-stat-label">{{ $t('cdr-dashboard.overview.manually-corrected') }}</span>
                <span class="small-stat-period">{{ $t('cdr-dashboard.overview.last-7-days') }}</span>
              </div>
            </div>
            <h4 class="small-stat-value">{{ stats.correctionCount || 0 }}</h4>
            <div class="small-stat-trend trend--correction" v-if="stats.correctionTrend != null">
              <v-icon x-small>{{ stats.correctionTrend > 0 ? 'mdi-trending-up' : 'mdi-trending-down' }}</v-icon>
              {{ stats.correctionTrend > 0 ? '+' : '' }}{{ stats.correctionTrend }}% {{ $t('cdr-dashboard.overview.vs-last-month') }}
            </div>
          </div>
        </div>

        <!-- Donut Chart -->
        <div class="donut-card">
          <div class="donut-header">
            <h3>{{ $t('cdr-dashboard.overview.decision-distribution') }}</h3>
            <v-icon small color="grey">mdi-information-outline</v-icon>
          </div>
          <div class="donut-container">
            <div class="donut-chart" :style="donutStyle">
              <div class="donut-center">
                <span class="donut-total-label">{{ $t('cdr-dashboard.overview.decided') }}</span>
                <span class="donut-total-value">{{ formatNumber(stats.decidedCount) }}</span>
              </div>
            </div>
          </div>
          <div class="donut-legend">
            <div class="legend-item legend-item--success" v-if="stats.validPercent">
              <span class="legend-dot"></span>
              <span class="legend-label">{{ $t('cdr-dashboard.overview.valid') }}</span>
              <span class="legend-value">{{ stats.validPercent }}%</span>
            </div>
            <div class="legend-item legend-item--primary" v-if="stats.autoFixPercent">
              <span class="legend-dot"></span>
              <span class="legend-label">{{ $t('cdr-dashboard.overview.auto-fixed') }}</span>
              <span class="legend-value">{{ stats.autoFixPercent }}%</span>
            </div>
            <div class="legend-item legend-item--warning" v-if="stats.blockedPercent">
              <span class="legend-dot"></span>
              <span class="legend-label">{{ $t('cdr-dashboard.overview.blocked') }}</span>
              <span class="legend-value">{{ stats.blockedPercent }}%</span>
            </div>
            <div class="legend-item legend-item--duplicate" v-if="stats.duplicatePercent">
              <span class="legend-dot"></span>
              <span class="legend-label">{{ $t('cdr-dashboard.overview.duplicate') }}</span>
              <span class="legend-value">{{ stats.duplicatePercent }}%</span>
            </div>
            <div class="legend-item legend-item--correction" v-if="stats.correctionPercent">
              <span class="legend-dot"></span>
              <span class="legend-label">{{ $t('cdr-dashboard.overview.corrected') }}</span>
              <span class="legend-value">{{ stats.correctionPercent }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Timeline Chart -->
      <div class="timeline-card">
        <div class="timeline-header">
          <div>
            <h3 class="timeline-title">{{ $t('cdr-dashboard.overview.validation-timeline') }}</h3>
            <p class="timeline-subtitle">{{ $t('cdr-dashboard.overview.timeline-subtitle') }}</p>
          </div>
          <div class="timeline-actions">
            <div class="timeline-legend">
              <span class="legend-dot legend-dot--success"></span> {{ $t('cdr-dashboard.overview.legend-valid') }}
              <span class="legend-dot legend-dot--primary ml-3"></span> {{ $t('cdr-dashboard.overview.legend-auto') }}
              <span class="legend-dot legend-dot--correction ml-3"></span> {{ $t('cdr-dashboard.overview.legend-corrected') }}
            </div>
            <v-btn text x-small color="primary" @click="$emit('go-to-tab', 'history')">
              {{ $t('cdr-dashboard.overview.view-history') }}
              <v-icon x-small right>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="timeline-chart">
          <highcharts v-if="hasTimelineData" :options="timelineChartOptions" />
          <div v-else class="empty-chart">
            <span>{{ $t('cdr-dashboard.overview.no-data') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Analyse des causes racines Section -->
    <div class="section-header">
      <h3 class="section-title">{{ $t('cdr-dashboard.overview.root-cause-analysis') }}</h3>
      <div class="section-line"></div>
    </div>

    <div class="top-issues-card">
      <div class="top-issues-header">
        <div class="top-issues-title">
          <div class="top-issues-icon">
            <v-icon>mdi-magnify-scan</v-icon>
          </div>
          <div>
            <h3>{{ $t('cdr-dashboard.overview.top-issues') }}</h3>
            <p>{{ $t('cdr-dashboard.overview.top-issues-description') }}</p>
          </div>
        </div>
        <div class="top-issues-tabs">
          <button
            v-for="tab in groupByTabs"
            :key="tab.value"
            :class="['tab-btn', { 'tab-btn--active': issuesGroupBy === tab.value }]"
            @click="issuesGroupBy = tab.value"
          >
            {{ tab.text }}
          </button>
        </div>
      </div>

      <div class="top-issues-filters">
        <button class="filter-chip filter-chip--danger">
          <v-icon x-small class="mr-1">mdi-fire</v-icon>
          {{ $t('cdr-dashboard.overview.most-blocking') }}
        </button>
        <button class="filter-chip filter-chip--primary">
          <v-icon x-small class="mr-1">mdi-cash</v-icon>
          {{ $t('cdr-dashboard.overview.tariff-impact') }}
        </button>
      </div>

      <table class="issues-table">
        <thead>
          <tr>
            <th>{{ $t('cdr-dashboard.overview.error-code-desc') }}</th>
            <th>{{ $t('cdr-dashboard.overview.severity') }}</th>
            <th class="text-right">{{ $t('cdr-dashboard.overview.occurrences') }}</th>
            <th class="text-right">{{ $t('cdr-dashboard.overview.financial-impact') }}</th>
            <th class="text-right">{{ $t('cdr-dashboard.overview.action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="issue in topIssues" :key="issue.key" @click="filterByIssue(issue)">
            <td>
              <div class="issue-code">
                <span class="code">{{ issue.key }}</span>
                <span class="description">{{ issue.description || getErrorDescription(issue.key) }}</span>
              </div>
            </td>
            <td>
              <span class="severity-badge" :class="getSeverityClass(issue.severity)">
                <v-icon x-small class="mr-1">{{ getSeverityIcon(issue.severity) }}</v-icon>
                {{ getSeverityLabel(issue.severity) }}
              </span>
            </td>
            <td class="text-right">
              <span class="occurrence-value">{{ issue.count }}</span>
            </td>
            <td class="text-right">
              <div class="impact-cell">
                <span class="impact-value">{{ formatCurrency(issue.financialImpact) }}</span>
                <span class="impact-risk" :class="getImpactRiskClass(issue.financialImpact)">
                  {{ getImpactRiskLabel(issue.financialImpact) }}
                </span>
              </div>
            </td>
            <td class="text-right">
              <button class="action-link" @click.stop="filterByIssue(issue)">
                {{ $t('cdr-dashboard.overview.view-cdrs') }}
                <v-icon x-small>mdi-arrow-right</v-icon>
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="top-issues-footer" v-if="topIssuesHasMore">
        <button class="load-more-btn" @click="loadMoreIssues" :disabled="topIssuesLoading">
          <v-progress-circular v-if="topIssuesLoading" indeterminate size="14" width="2" class="mr-2" />
          <template v-else>
            {{ $t('cdr-dashboard.overview.load-more') }}
            <v-icon small>mdi-chevron-down</v-icon>
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import PendingCdrRepository from '@repository/PendingCdrRepository'
import { Chart } from 'highcharts-vue'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/fr'

dayjs.extend(relativeTime)
dayjs.locale('fr')

export default {
  name: 'CdrOverviewTab',

  components: {
    highcharts: Chart,
  },

  props: {
    period: {
      type: String,
      default: '7d',
    },
    backlog: {
      type: Object,
      default: () => ({
        count: 0,
        oldestPendingCdr: null,
      }),
    },
  },

  data() {
    return {
      loading: false,
      issuesGroupBy: 'code',

      stats: {
        totalCount: 0,
        decidedCount: 0,
        validCount: 0,
        blockedCount: 0,
        autoFixedCount: 0,
        duplicateCount: 0,
        correctionCount: 0,
        validPercent: 0,
        autoFixPercent: 0,
        blockedPercent: 0,
        duplicatePercent: 0,
        correctionPercent: 0,
        errorRate: null,
        autoFixRate: null,
        blockedTrend: null,
        autoFixTrend: null,
        correctionTrend: null,
      },

      dailyData: [],
      topIssues: [],
      dominantError: null,
      topIssuesLoading: false,
      topIssuesHasMore: true,
    }
  },

  computed: {
    groupByTabs() {
      return [
        { text: this.$t('cdr-dashboard.overview.by-code'), value: 'code' },
        { text: this.$t('cdr-dashboard.overview.by-evse'), value: 'evse' },
        { text: this.$t('cdr-dashboard.overview.by-source'), value: 'source' },
      ]
    },

    oldestDays() {
      if (!this.backlog?.oldestPendingCdr?.createdAt) return 0
      return dayjs().diff(dayjs(this.backlog.oldestPendingCdr.createdAt), 'day')
    },

    hasTimelineData() {
      return this.dailyData && this.dailyData.length > 0
    },

    donutStyle() {
      const valid = this.stats.validPercent || 0
      const autoFix = this.stats.autoFixPercent || 0
      const blocked = this.stats.blockedPercent || 0
      const duplicate = this.stats.duplicatePercent || 0
      const corrected = this.stats.correctionPercent || 0

      if (this.stats.decidedCount === 0) {
        return { background: '#E5E7EB' }
      }

      const s1 = valid
      const s2 = s1 + autoFix
      const s3 = s2 + blocked
      const s4 = s3 + duplicate

      return {
        background: `conic-gradient(
          #059669 0% ${s1}%,
          #4F46E5 ${s1}% ${s2}%,
          #D97706 ${s2}% ${s3}%,
          #6B7280 ${s3}% ${s4}%,
          #F97316 ${s4}% 100%
        )`
      }
    },

    timelineChartOptions() {
      const series = this.buildTimelineSeries()
      return {
        chart: {
          type: 'areaspline',
          height: 300,
          backgroundColor: 'transparent',
          style: { fontFamily: 'Inter, sans-serif' },
        },
        title: { text: null },
        credits: { enabled: false },
        colors: ['#059669', '#4F46E5', '#F97316'],
        xAxis: {
          type: 'datetime',
          labels: {
            format: '{value:%d %b}',
            style: { fontSize: '10px', fontWeight: '600', textTransform: 'uppercase' },
          },
          gridLineWidth: 0,
          lineColor: '#E5E7EB',
        },
        yAxis: {
          title: { text: null },
          gridLineColor: '#F3F4F6',
          labels: { style: { fontSize: '10px' } },
        },
        legend: { enabled: false },
        tooltip: {
          shared: true,
          backgroundColor: '#1F2937',
          borderColor: '#1F2937',
          borderRadius: 8,
          style: { color: '#fff', fontSize: '11px' },
          headerFormat: '<span style="font-size: 10px; font-weight: bold; text-transform: uppercase;">{point.key:%d %B}</span><br/>',
          pointFormat: '<span style="color:{series.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>',
        },
        plotOptions: {
          areaspline: {
            fillOpacity: 0.1,
            lineWidth: 2,
            marker: { enabled: false, radius: 3, symbol: 'circle' },
          },
        },
        series,
      }
    },
  },

  watch: {
    period() {
      this.loadData()
    },
    issuesGroupBy() {
      this.loadTopIssues()
    },
  },

  mounted() {
    this.loadData()
  },

  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadStats(),
          this.loadTopIssues(),
        ])
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },

    refresh() {
      this.loadData()
    },

    async loadStats() {
      try {
        const result = await PendingCdrRepository.getStats({
          period: this.period,
          byDay: true,
          withTrends: true,
        })

        // Compteurs bruts
        const valid = (result.validCount || 0) + (result.validWithWarningsCount || 0)
        const autoFixed = (result.autoFixedCount || 0) + (result.autoAcceptedCount || 0)
        const blocked = result.blockedCount || 0
        const duplicate = result.duplicateCount || 0
        const corrected = result.correctionCount || 0

        // Le dénominateur = somme des CDR décidés (pas totalCount qui inclut les pending)
        const decided = valid + autoFixed + blocked + duplicate + corrected
        const denominator = decided || 1

        const rawPercents = [
          (valid / denominator) * 100,
          (autoFixed / denominator) * 100,
          (blocked / denominator) * 100,
          (duplicate / denominator) * 100,
          (corrected / denominator) * 100,
        ]
        const percents = this.roundPercents(rawPercents)

        this.stats = {
          totalCount: result.totalCount || 0,
          decidedCount: decided,
          validCount: valid,
          blockedCount: blocked,
          autoFixedCount: autoFixed,
          duplicateCount: duplicate,
          correctionCount: corrected,
          validPercent: percents[0],
          autoFixPercent: percents[1],
          blockedPercent: percents[2],
          duplicatePercent: percents[3],
          correctionPercent: percents[4],
          errorRate: result.errorRate ?? null,
          autoFixRate: result.autoFixRate ?? null,
          blockedTrend: result.blockedTrend ?? null,
          autoFixTrend: result.autoFixTrend ?? null,
          correctionTrend: result.correctionTrend ?? null,
        }

        this.dailyData = result.daily || []
      } catch (error) {
        console.error('Failed to load stats:', error)
      }
    },

    async loadTopIssues() {
      try {
        const result = await PendingCdrRepository.getTopIssues({
          groupBy: this.issuesGroupBy,
          period: this.period,
          limit: 10,
          offset: 0,
        })
        this.topIssues = result.items || []
        this.topIssuesHasMore = (this.topIssues.length >= 10)
        if (this.topIssues.length > 0) {
          this.dominantError = this.topIssues[0].key?.replace('ERR_', '').replace(/_/g, ' ')
        }
      } catch (error) {
        console.error('Failed to load top issues:', error)
      }
    },

    buildTimelineSeries() {
      if (!this.dailyData || this.dailyData.length === 0) return []

      const validData = []
      const autoData = []
      const correctionData = []

      for (const day of this.dailyData) {
        const timestamp = new Date(day.date).getTime()
        validData.push([timestamp, day.validCount || 0])
        autoData.push([timestamp, day.autoFixedCount || 0])
        correctionData.push([timestamp, day.correctionCount || 0])
      }

      return [
        { name: this.$t('cdr-dashboard.overview.legend-valid'), data: validData },
        { name: this.$t('cdr-dashboard.overview.legend-auto'), data: autoData },
        { name: this.$t('cdr-dashboard.overview.legend-corrected'), data: correctionData },
      ]
    },

    filterByIssue(issue) {
      const query = {}
      if (this.issuesGroupBy === 'code') {
        query.issueCodes = [issue.key]
      } else if (this.issuesGroupBy === 'source') {
        query.sources = [issue.key]
      } else if (this.issuesGroupBy === 'evse') {
        query.evseId = issue.key
      }
      this.$emit('go-to-tab', 'pending', query)
    },

    async loadMoreIssues() {
      if (this.topIssuesLoading || !this.topIssuesHasMore) return
      this.topIssuesLoading = true
      try {
        const result = await PendingCdrRepository.getTopIssues({
          groupBy: this.issuesGroupBy,
          period: this.period,
          limit: 10,
          offset: this.topIssues.length,
        })
        const newItems = result.items || []
        this.topIssues = [...this.topIssues, ...newItems]
        this.topIssuesHasMore = (newItems.length >= 10)
      } catch (error) {
        console.error('Failed to load more issues:', error)
      } finally {
        this.topIssuesLoading = false
      }
    },

    getErrorDescription(code) {
      const key = `cdr-dashboard.overview.error-descriptions.${code}`
      const translated = this.$t(key)
      return translated !== key ? translated : ''
    },

    getSeverityClass(severity) {
      return {
        'severity-badge--blocking': severity === 'blocking',
        'severity-badge--warning': severity === 'warning',
      }
    },

    getSeverityIcon(severity) {
      return severity === 'blocking' ? 'mdi-gavel' : 'mdi-alert'
    },

    getSeverityLabel(severity) {
      return severity === 'blocking'
        ? this.$t('cdr-dashboard.overview.severity-blocking')
        : this.$t('cdr-dashboard.overview.severity-warning')
    },

    getImpactRiskClass(amount) {
      if (!amount) return 'risk--low'
      if (amount > 500) return 'risk--high'
      if (amount > 100) return 'risk--medium'
      return 'risk--low'
    },

    getImpactRiskLabel(amount) {
      if (!amount) return this.$t('cdr-dashboard.overview.risk-low')
      if (amount > 500) return this.$t('cdr-dashboard.overview.risk-high')
      if (amount > 100) return this.$t('cdr-dashboard.overview.risk-medium')
      return this.$t('cdr-dashboard.overview.risk-low')
    },

    formatNumber(value) {
      if (value >= 1000) {
        return (value / 1000).toFixed(1) + 'k'
      }
      return value?.toString() || '0'
    },

    formatCurrency(value) {
      if (!value) return '-'
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: 'EUR',
      }).format(value)
    },

    // Largest remainder method : arrondit les pourcentages en gardant la somme = 100
    roundPercents(rawValues) {
      const total = rawValues.reduce((a, b) => a + b, 0)
      if (total === 0) return rawValues.map(() => 0)

      const floored = rawValues.map(v => Math.floor(v))
      let remainder = 100 - floored.reduce((a, b) => a + b, 0)
      const decimals = rawValues.map((v, i) => ({ i, d: v - floored[i] }))
      decimals.sort((a, b) => b.d - a.d)

      for (let j = 0; j < remainder; j++) {
        floored[decimals[j].i]++
      }
      return floored
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #4F46E5;
$danger: #DC2626;
$success: #059669;
$warning: #D97706;
$text-main: #111827;
$text-secondary: #4B5563;
$border-color: #E5E7EB;
$bg-subtle: #F3F4F6;

.cdr-overview-tab {
  padding: 24px;
}

// KPI Grid
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 32px;
}

.kpi-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid $border-color;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  }

  &--primary {
    border-color: rgba($danger, 0.2);
  }

  &--danger {
    border-color: rgba($danger, 0.2);
  }
}

.kpi-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.kpi-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &--primary {
    background: rgba($primary, 0.1);
    color: $primary;
  }

  &--danger {
    background: rgba($danger, 0.1);
    color: $danger;
  }
}

.kpi-badge {
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 6px 10px;
  border-radius: 20px;

  &--danger {
    background: $danger;
    color: white;
    animation: pulse 2s infinite;
  }

  &--danger-outline {
    background: rgba($danger, 0.1);
    color: $danger;
    border: 1px solid rgba($danger, 0.2);
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.kpi-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: $text-secondary;
  margin: 0 0 4px 0;
}

.kpi-value {
  font-size: 48px;
  font-weight: 900;
  color: $text-main;
  margin: 0;
  line-height: 1;
  letter-spacing: -2px;

  &--danger {
    color: $danger;
  }
}

.kpi-subtitle {
  font-size: 10px;
  color: $text-secondary;
  background: $bg-subtle;
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 8px;
  display: inline-block;
}

// Section Header
.section-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: $text-secondary;
  white-space: nowrap;
  margin: 0;
}

.section-line {
  flex: 1;
  height: 1px;
  background: $border-color;
}

// Stats Grid
.stats-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 32px;
  margin-bottom: 48px;
}

.stats-column {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.small-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.small-stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid $border-color;
}

.small-stat-header {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  color: $text-secondary;
}

.small-stat-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.small-stat-period {
  display: block;
  font-size: 9px;
  color: rgba($text-secondary, 0.7);
}

.small-stat-value {
  font-size: 28px;
  font-weight: 900;
  color: rgba($text-secondary, 0.7);
  margin: 0;
}

.small-stat-trend {
  font-size: 10px;
  font-weight: 600;
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;

  &.trend--success { color: $success; }
  &.trend--primary { color: $primary; }
  &.trend--correction { color: #F97316; }
}

// Donut Card
.donut-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid $border-color;
}

.donut-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  h3 {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    margin: 0;
  }
}

.donut-container {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.donut-chart {
  width: 176px;
  height: 176px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.donut-center {
  width: 112px;
  height: 112px;
  background: white;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.donut-total-label {
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase;
  color: $text-secondary;
}

.donut-total-value {
  font-size: 24px;
  font-weight: 900;
  color: $text-main;
}

.donut-legend {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 8px;

  &--success {
    background: rgba($success, 0.05);
    border: 1px solid rgba($success, 0.1);
    .legend-dot { background: $success; }
    .legend-value { color: $success; }
  }

  &--primary {
    background: rgba($primary, 0.05);
    border: 1px solid rgba($primary, 0.1);
    .legend-dot { background: $primary; }
    .legend-value { color: $primary; }
  }

  &--warning {
    background: rgba($warning, 0.05);
    border: 1px solid rgba($warning, 0.1);
    .legend-dot { background: $warning; }
    .legend-value { color: $warning; }
  }

  &--duplicate {
    background: rgba(#6B7280, 0.05);
    border: 1px solid rgba(#6B7280, 0.1);
    .legend-dot { background: #6B7280; }
    .legend-value { color: #6B7280; }
  }

  &--correction {
    background: rgba(#F97316, 0.05);
    border: 1px solid rgba(#F97316, 0.1);
    .legend-dot { background: #F97316; }
    .legend-value { color: #F97316; }
  }
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}

.legend-label {
  flex: 1;
  font-weight: 500;
}

.legend-value {
  font-weight: 900;
}

// Timeline Card
.timeline-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid $border-color;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
}

.timeline-title {
  font-size: 16px;
  font-weight: 900;
  color: $text-main;
  margin: 0 0 4px 0;
}

.timeline-subtitle {
  font-size: 12px;
  color: $text-secondary;
  margin: 0;
}

.timeline-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.timeline-legend {
  font-size: 10px;
  font-weight: 700;
  color: $text-secondary;
  background: $bg-subtle;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid $border-color;

  .legend-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;

    &--success { background: $success; }
    &--primary { background: $primary; }
    &--correction { background: #F97316; }
  }
}

.timeline-chart {
  min-height: 300px;
}

.empty-chart {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 300px;
  background: $bg-subtle;
  border-radius: 8px;
  color: $text-secondary;
}

// Top Issues Card
.top-issues-card {
  background: white;
  border-radius: 16px;
  border: 1px solid $border-color;
  overflow: hidden;
}

.top-issues-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid $border-color;
}

.top-issues-title {
  display: flex;
  align-items: center;
  gap: 16px;

  h3 {
    font-size: 18px;
    font-weight: 900;
    margin: 0;
  }

  p {
    font-size: 12px;
    color: $text-secondary;
    margin: 4px 0 0 0;
  }
}

.top-issues-icon {
  width: 44px;
  height: 44px;
  background: #1F2937;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.top-issues-tabs {
  display: flex;
  background: $bg-subtle;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid $border-color;
}

.tab-btn {
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  border: none;
  background: none;
  color: $text-secondary;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: $text-main;
  }

  &--active {
    background: white;
    color: $text-main;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    border: 1px solid $border-color;
  }
}

.top-issues-filters {
  display: flex;
  gap: 8px;
  padding: 16px 24px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 20px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  &--danger {
    background: rgba($danger, 0.1);
    color: $danger;
    border-color: rgba($danger, 0.2);

    &:hover {
      background: rgba($danger, 0.2);
    }
  }

  &--primary {
    background: rgba($primary, 0.1);
    color: $primary;
    border-color: rgba($primary, 0.2);

    &:hover {
      background: rgba($primary, 0.2);
    }
  }
}

// Issues Table
.issues-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: rgba($bg-subtle, 0.5);

    th {
      padding: 20px 32px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: $text-secondary;
      border-bottom: 1px solid $border-color;
      text-align: left;
    }
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: rgba($primary, 0.03);
    }

    td {
      padding: 24px 32px;
      border-bottom: 1px solid $border-color;
    }
  }
}

.issue-code {
  .code {
    display: block;
    font-family: ui-monospace, monospace;
    font-size: 13px;
    font-weight: 900;
    color: $primary;
  }

  .description {
    display: block;
    font-size: 12px;
    color: $text-secondary;
    margin-top: 4px;
  }
}

.severity-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 8px;

  &--blocking {
    background: $danger;
    color: white;
  }

  &--warning {
    background: rgba($warning, 0.1);
    color: $warning;
    border: 1px solid rgba($warning, 0.3);
  }
}

.occurrence-value {
  font-size: 16px;
  font-weight: 900;
  font-style: italic;
}

.impact-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.impact-value {
  font-size: 13px;
  font-weight: 900;
  border-bottom: 1px dotted $text-secondary;
}

.impact-risk {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  margin-top: 2px;

  &.risk--high { color: $danger; }
  &.risk--medium { color: $warning; }
  &.risk--low { color: $text-secondary; }
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 900;
  color: $primary;
  background: white;
  border: 1px solid $border-color;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: $primary;
    color: white;
    border-color: $primary;
  }
}

.top-issues-footer {
  display: flex;
  justify-content: center;
  padding: 24px;
  background: rgba($bg-subtle, 0.5);
  border-top: 1px solid $border-color;
}

.load-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: $primary;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: darken($primary, 10%);
  }
}

// Responsive
@media (max-width: 1024px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .actions-banner {
    flex-direction: column;
    gap: 24px;
  }

  .banner-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
